import { cn } from "@/lib/utils";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-accent hover:bg-accent-hover text-accent-contrast",
  // Un contour et non un fond : posé sur une carte, le fond « carte » de la
  // version d'avant se confondait avec elle, et « Encaisser » ou « Annuler »
  // se lisaient comme du texte.
  secondary: "border-border-strong text-text-primary hover:bg-bg-card-hover",
  ghost: "text-text-secondary hover:text-text-primary hover:bg-bg-card-hover",
  danger: "bg-danger/10 hover:bg-danger/20 text-danger-ink",
};

/**
 * `sm` est la taille des actions de ligne — « Relancer », « Encaisser »,
 * « Noter » — partout la même : 32 px de haut, et une zone d'appui qui déborde
 * du bouton pour atteindre la taille d'un doigt sans grossir la ligne. Avant,
 * les mêmes gestes allaient de 28 à 48 px selon l'écran.
 *
 * Le jaune (`primary`) d'une action de ligne est réservé aux files de
 * décision — ce qui attend votre accord, la file du soir, les congés à
 * trancher. Ailleurs, une ligne porte un contour : une colonne de boutons
 * jaunes ne dit plus où regarder.
 */
const SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs whitespace-nowrap relative after:absolute after:-inset-x-1 after:-inset-y-1.5",
  md: "px-4 py-2.5 text-sm touch-target",
  lg: "px-5 py-3 text-base touch-target",
};

/** Les classes d'un bouton, pour un lien qui doit en avoir l'air. */
export function buttonClasses({
  variant = "primary",
  size = "md",
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {}): string {
  return cn(
    // Une bordure sur toutes les variantes, transparente sauf sur le contour :
    // un bouton jaune et un bouton à contour côte à côte ont la même hauteur.
    "inline-flex items-center justify-center gap-2 font-semibold rounded-xl text-center border border-transparent",
    // Un libellé qui passe sur deux lignes ne doit pas écraser son icône.
    "[&>svg]:shrink-0",
    // L'appui se voit au contact du doigt, pas au relâchement : le bouton
    // s'enfonce d'un rien. Couleur et échelle partagent la même transition
    // courte, pour que le retour ne traîne pas derrière le geste.
    "transition-[color,background-color,transform] duration-100 ease-out active:scale-[0.97]",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100",
    VARIANT[variant],
    SIZE[size],
    className,
  );
}
